import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../../components/ui/dialog';
import {
  ArrowLeft,
  Play,
  Pause,
  Trash2,
  Users,
  FolderKanban,
  FileText,
  Calendar,
  Receipt,
  MessageSquare,
  Clock,
  Star,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Building2,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

const DemoModePage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [purging, setPurging] = useState(false);
  
  const [demoStatus, setDemoStatus] = useState({
    enabled: false,
    last_activated: null,
    last_deactivated: null,
    stats: {}
  });
  
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState('');

  useEffect(() => {
    fetchDemoStatus();
  }, []);

  const fetchDemoStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get('/settings/demo-mode');
      setDemoStatus(res.data);
    } catch (error) {
      console.error('Error fetching demo status:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      const res = await api.post('/settings/demo-mode/activate');
      if (res.data.success) {
        toast.success('Mode démo activé avec succès !');
        setShowActivateDialog(false);
        fetchDemoStatus();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'activation');
    } finally {
      setActivating(false);
    }
  };

  const handleDeactivate = async () => {
    setDeactivating(true);
    try {
      const res = await api.post('/settings/demo-mode/deactivate');
      if (res.data.success) {
        toast.success(res.data.message);
        setShowDeactivateDialog(false);
        fetchDemoStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la désactivation');
    } finally {
      setDeactivating(false);
    }
  };

  const handlePurgeAll = async () => {
    if (purgeConfirmText !== 'PURGER') {
      toast.error('Veuillez taper "PURGER" pour confirmer');
      return;
    }
    
    setPurging(true);
    try {
      const res = await api.post('/settings/demo-mode/purge-all');
      if (res.data.success) {
        toast.success(res.data.message);
        setShowPurgeDialog(false);
        setPurgeConfirmText('');
        fetchDemoStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la purge');
    } finally {
      setPurging(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statCards = [
    { key: 'clients', label: 'Clients', icon: Users, color: 'blue' },
    { key: 'projects', label: 'Projets', icon: FolderKanban, color: 'emerald' },
    { key: 'invoices', label: 'Factures', icon: Receipt, color: 'amber' },
    { key: 'appointments', label: 'RDV', icon: Calendar, color: 'violet' },
    { key: 'tickets', label: 'Tickets', icon: MessageSquare, color: 'rose' },
    { key: 'reports', label: 'Rapports', icon: FileText, color: 'indigo' },
    { key: 'surveys', label: 'Enquêtes', icon: Star, color: 'yellow' },
    { key: 'time_entries', label: 'Temps', icon: Clock, color: 'cyan' },
    { key: 'payment_schedules', label: 'Échéanciers', icon: Receipt, color: 'orange' },
  ];

  const demoCompanies = [
    { name: 'BESSON SOLUTIONS', email: 'contact@besson-solutions.fr' },
    { name: 'ILIKO PRESTO', email: 'direction@ilikopresto.com' },
    { name: 'SAS OTOP', email: 'contact@sas-otop.fr' },
  ];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                Mode Démo
              </h1>
              <p className="text-sm text-slate-500">
                Gérez le contenu de démonstration de l'application
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchDemoStatus}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Main Status Card */}
        <Card className={cn(
          "border-2 transition-all",
          demoStatus.enabled 
            ? "border-emerald-300 bg-gradient-to-br from-emerald-50 to-green-50" 
            : "border-slate-200"
        )}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={cn(
                  "w-16 h-16 rounded-2xl flex items-center justify-center",
                  demoStatus.enabled 
                    ? "bg-gradient-to-br from-emerald-500 to-green-500" 
                    : "bg-gradient-to-br from-slate-400 to-slate-500"
                )}>
                  {demoStatus.enabled ? (
                    <Play className="w-8 h-8 text-white" />
                  ) : (
                    <Pause className="w-8 h-8 text-white" />
                  )}
                </div>
                <div>
                  <CardTitle className="text-xl">
                    Mode Démo {demoStatus.enabled ? 'Activé' : 'Désactivé'}
                  </CardTitle>
                  <CardDescription>
                    {demoStatus.enabled 
                      ? 'Les données de démonstration sont visibles dans l\'application'
                      : 'Activez le mode démo pour afficher du contenu de démonstration'}
                  </CardDescription>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                {demoStatus.enabled ? (
                  <Button 
                    variant="destructive" 
                    onClick={() => setShowDeactivateDialog(true)}
                    className="gap-2"
                  >
                    <Pause className="w-4 h-4" />
                    Désactiver
                  </Button>
                ) : (
                  <Button 
                    onClick={() => setShowActivateDialog(true)}
                    style={{ backgroundColor: theme.colors?.accent }}
                    className="gap-2"
                  >
                    <Play className="w-4 h-4" />
                    Activer le mode démo
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          
          {demoStatus.enabled && (
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Activé le :</span>
                  <span className="ml-2 font-medium">{formatDate(demoStatus.last_activated)}</span>
                </div>
                {demoStatus.last_deactivated && (
                  <div>
                    <span className="text-slate-500">Dernière désactivation :</span>
                    <span className="ml-2 font-medium">{formatDate(demoStatus.last_deactivated)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Stats Grid */}
        {demoStatus.enabled && Object.keys(demoStatus.stats).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Données de démonstration créées</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
                {statCards.map((stat) => {
                  const Icon = stat.icon;
                  const count = demoStatus.stats[stat.key] || 0;
                  return (
                    <div 
                      key={stat.key}
                      className={cn(
                        "p-4 rounded-xl text-center",
                        `bg-${stat.color}-50`
                      )}
                    >
                      <Icon className={cn("w-6 h-6 mx-auto mb-2", `text-${stat.color}-500`)} />
                      <p className={cn("text-2xl font-bold", `text-${stat.color}-700`)}>{count}</p>
                      <p className="text-xs text-slate-600">{stat.label}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Demo Companies Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Entreprises de démonstration
            </CardTitle>
            <CardDescription>
              Ces 3 clients fictifs seront créés avec le mode démo (mot de passe : demo123)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {demoCompanies.map((company, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <p className="font-bold text-slate-800">{company.name}</p>
                  <p className="text-sm text-slate-500">{company.email}</p>
                  <p className="text-xs text-slate-400 mt-1">📍 Istres (13800)</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-red-200 bg-red-50/50">
          <CardHeader>
            <CardTitle className="text-lg text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Zone de danger
            </CardTitle>
            <CardDescription className="text-red-600">
              Actions irréversibles - À utiliser avec précaution
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 rounded-xl bg-white border border-red-200">
              <div>
                <p className="font-semibold text-slate-800">Purge complète</p>
                <p className="text-sm text-slate-500">
                  Supprimer TOUTES les données (projets, factures, RDV, etc.) - Conserve les utilisateurs
                </p>
              </div>
              <Button 
                variant="destructive" 
                onClick={() => setShowPurgeDialog(true)}
                className="gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Purger tout
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activate Dialog */}
      <Dialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-emerald-500" />
              Activer le mode démo
            </DialogTitle>
            <DialogDescription>
              Cette action va créer des données de démonstration :
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 py-4">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <span>3 clients fictifs (BESSON SOLUTIONS, ILIKO PRESTO, SAS OTOP)</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <span>6 projets avec tâches et livrables</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <span>Factures, échéanciers, RDV</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <span>Tickets, rapports, enquêtes satisfaction</span>
            </div>
          </div>
          
          <p className="text-sm text-slate-500">
            Toutes ces données seront marquées <Badge variant="outline">is_demo</Badge> et pourront être supprimées en un clic.
          </p>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivateDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleActivate}
              disabled={activating}
              style={{ backgroundColor: theme.colors?.accent }}
            >
              {activating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Activer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Dialog */}
      <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pause className="w-5 h-5 text-amber-500" />
              Désactiver le mode démo
            </DialogTitle>
            <DialogDescription>
              Cette action va supprimer toutes les données créées par le mode démo.
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
            <p className="text-sm text-amber-800">
              <strong>Note :</strong> Seules les données marquées "démo" seront supprimées. 
              Vos vraies données client resteront intactes.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeactivateDialog(false)}>
              Annuler
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeactivate}
              disabled={deactivating}
            >
              {deactivating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Pause className="w-4 h-4 mr-2" />
              )}
              Désactiver et supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purge All Dialog */}
      <Dialog open={showPurgeDialog} onOpenChange={setShowPurgeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Purge complète - Action irréversible
            </DialogTitle>
            <DialogDescription>
              Cette action va supprimer TOUTES les données de l'application.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-xl bg-red-50 border border-red-200">
              <p className="text-sm text-red-800 font-medium mb-2">
                Seront supprimés :
              </p>
              <ul className="text-sm text-red-700 space-y-1">
                <li>• Tous les projets et tâches</li>
                <li>• Toutes les factures et échéanciers</li>
                <li>• Tous les RDV et tickets</li>
                <li>• Tous les rapports et enquêtes</li>
                <li>• Tout le temps tracké</li>
                <li>• Tous les contrats et prospects</li>
              </ul>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">
                Tapez <span className="font-bold text-red-600">PURGER</span> pour confirmer :
              </label>
              <input
                type="text"
                value={purgeConfirmText}
                onChange={(e) => setPurgeConfirmText(e.target.value)}
                className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="PURGER"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPurgeDialog(false); setPurgeConfirmText(''); }}>
              Annuler
            </Button>
            <Button 
              variant="destructive"
              onClick={handlePurgeAll}
              disabled={purging || purgeConfirmText !== 'PURGER'}
            >
              {purging ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Purger définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default DemoModePage;
